import { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Layout, Typography, Card, Flex, Input, Spin, Space, Button, Grid } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";

import { formatDateTime } from "../../utils/appUtils";
import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import EventHeader from "../../components/Events/EventHeader";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import TableSelectorPanel from "../../components/Events/TableSelectorPanel";
import {
  fetchTicketCategories,
  selectTicketCategories,
  fetchEventBySlug,
  clearPublicEventBySlug,
  selectPublicEventBySlug,
  selectFetchEventBySlugStatus,
  fetchEventTables,
  selectEventTables,
  selectFetchEventTablesLoading,
  clearEventTables,
} from "../../redux/eventSlice";
import {
  setBookedSeatsData,
  selectBookedSeatsData,
  setSelectedTables,
  selectSelectedTables,
  resetBookedSeatsData,
  resetSelectedTables,
} from "../../redux/orderSlice";

const { Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PASS_TIER_ORDER = ["silver", "gold", "premium"];

function passTierSortIndex(categoryName) {
  const n = String(categoryName || "").toLowerCase();
  const i = PASS_TIER_ORDER.findIndex((tier) => n.includes(tier));
  return i === -1 ? PASS_TIER_ORDER.length : i;
}

export default function TicketBookingPage() {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug } = useParams();
  const { state } = useLocation();

  const [event, setEvent] = useState(state?.event || null);
  const [numberOfSeatSelected, setNumberOfSeatSelected] = useState({});
  const [selectedTables, setSelectedTablesLocal] = useState([]);

  const publicEventBySlug = useSelector(selectPublicEventBySlug);
  const slugFetchStatus = useSelector(selectFetchEventBySlugStatus);
  const ticketCategories = useSelector(selectTicketCategories);
  const bookedSeatsData = useSelector(selectBookedSeatsData);
  const storedSelectedTables = useSelector(selectSelectedTables);
  const eventTables = useSelector(selectEventTables);
  const fetchEventTablesLoading = useSelector(selectFetchEventTablesLoading);

  const ticketMode = event?.ticket_mode || "per_seat";
  const isPerTable = ticketMode === "per_table";

  const eventSlug = event?.slug || slug;
  const loadingStatus = event ? "completed" : slugFetchStatus;

  const handleBookTicket = () => {
    if (isPerTable) {
      dispatch(resetBookedSeatsData());
      dispatch(setSelectedTables(selectedTables));
    } else {
      dispatch(resetSelectedTables());
      dispatch(setBookedSeatsData(numberOfSeatSelected));
    }
    navigate(`/event-details/payment/${eventSlug}`, { state: { event } });
  };

  const hasSelection = isPerTable
    ? selectedTables.length > 0
    : Object.keys(numberOfSeatSelected).filter((key) => numberOfSeatSelected[key] > 0).length > 0;

  useEffect(() => {
    dispatch(fetchTicketCategories());

    if (state?.event) {
      setEvent(state.event);
      return undefined;
    }

    if (!slug) return undefined;

    dispatch(clearPublicEventBySlug());
    dispatch(fetchEventBySlug(slug));

    return () => {
      dispatch(clearPublicEventBySlug());
      dispatch(clearEventTables());
    };
  }, [dispatch, slug, state?.event]);

  useEffect(() => {
    if (!state?.event && publicEventBySlug) {
      setEvent(publicEventBySlug);
    }
  }, [publicEventBySlug, state?.event]);

  useEffect(() => {
    if (event?.id && isPerTable) {
      dispatch(fetchEventTables(event.id));
    }
  }, [dispatch, event?.id, isPerTable]);

  useEffect(() => {
    if (storedSelectedTables) {
      setSelectedTablesLocal(storedSelectedTables);
    }
  }, [storedSelectedTables]);

  useEffect(() => {
    if (bookedSeatsData) {
      setNumberOfSeatSelected(bookedSeatsData);
    } else if (!isPerTable && event?.tickets && event?.tickets.length > 0) {
      event?.tickets.forEach((ticket) => {
        setNumberOfSeatSelected((prev) => ({ ...prev, [ticket?.ticket_category_id]: 0 }));
      });
    }
  }, [event, bookedSeatsData, isPerTable]);

  const sortedTickets = useMemo(() => {
    if (!event?.tickets?.length) return [];
    const cats = ticketCategories || [];
    return [...event.tickets].sort((a, b) => {
      const nameA = cats.find((c) => c.id === a.ticket_category_id)?.category_name || "";
      const nameB = cats.find((c) => c.id === b.ticket_category_id)?.category_name || "";
      return passTierSortIndex(nameA) - passTierSortIndex(nameB);
    });
  }, [event?.tickets, ticketCategories]);

  const goToEventDetailsPage = () => {
    if (eventSlug) navigate(`/event-details/${eventSlug}`, { state: { event } });
  };

  const venueName = event?.venue?.name || "Venue Name";
  const startDateTime = formatDateTime(event?.event_start_date_time);

  if (loadingStatus === "loading") {
    return (
      <>
        <Header isHome={false}/>
        <Content className="topheaderLayout" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" tip="Loading ticket booking page..." />
        </Content>
        <Footer />
      </>
    );
  }

  return (
    <>
      <HelmetWrapper
          title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai"
          description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability,
          and city-specific visibility for organizers and users."
          keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata,
          online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai,
          Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai,
          Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
      />

      <Header isHome={false}/>

      <Content className="topheaderLayout">
        <EventHeader
          event={event}
          customizedButtons={[
            {
              "text": "Go Back",
              "onClick": goToEventDetailsPage,
              "disabled": false
            },
            {
              "text": isPerTable ? "Book Tables" : "Book Ticket",
              "onClick": handleBookTicket,
              "disabled": !hasSelection,
              "type": "primary",
              "className": "btnGradient"
            }
          ]}
        />

        {isPerTable ? (
          <TableSelectorPanel
            eventTables={eventTables}
            loading={fetchEventTablesLoading}
            selectedTables={selectedTables}
            onSelectionChange={setSelectedTablesLocal}
          />
        ) : (
          <Flex justify="flex-start" align="start" gap={12} vertical={!screens.md} className="ticketBookingCards">
            {sortedTickets.length > 0 && sortedTickets.map((ticket) => (
                <Card
                  key={ticket?.id != null ? String(ticket.id) : `ticket-${ticket?.ticket_category_id}`}
                  className="orderSummaryCard"
                >
                  <Flex vertical gap={12} className="orderItem orderBookingCardInner">
                    <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12} className="orderCardHeadline">
                      <Title level={4} className="orderCardPassTitle">
                        {ticketCategories && ticketCategories.length > 0 ?
                          `${ticketCategories.find(category => category.id === ticket?.ticket_category_id)?.category_name} Pass`
                          :
                          "Free Pass"
                        }
                      </Title>
                      <Text className="orderPrice orderPriceTop">
                        {`₹${numberOfSeatSelected[ticket?.ticket_category_id] ? ticket?.price * numberOfSeatSelected?.[ticket?.ticket_category_id] : 0}`}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="flex-end" wrap="wrap" gap={16} className="orderCardDetailsRow">
                      <div className="orderMeta orderMetaInBookingCard">
                        <Text>{venueName}</Text>
                        <Text>{startDateTime}</Text>
                        <Text>{ticket?.price}</Text>
                      </div>
                      <div className="orderQuantityRow">
                        <Text className="orderQuantityLabel">Quantity:</Text>
                        <Space.Compact>
                          <Button
                            type="text"
                            icon={<MinusOutlined />}
                            onClick={() => {
                              const parsedValue = Number(numberOfSeatSelected?.[ticket?.ticket_category_id]) - 1 || 0;
                              setNumberOfSeatSelected((prev) => {
                                const next = { ...(prev || {}) };
                                next[ticket?.ticket_category_id] = parsedValue < 0 ? 0 : parsedValue;
                                return next;
                              });
                            }}
                            disabled={numberOfSeatSelected?.[ticket?.ticket_category_id] === 0}
                          />
                          <Input
                            value={numberOfSeatSelected?.[ticket?.ticket_category_id]}
                            readOnly
                            style={{
                              width: 40,
                              textAlign: "center",
                              pointerEvents: "none"
                            }}
                          />
                          <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              const parsedValue = Number(numberOfSeatSelected?.[ticket?.ticket_category_id]) + 1 || 0;
                              setNumberOfSeatSelected((prev) => {
                                const next = { ...(prev || {}) };
                                next[ticket?.ticket_category_id] = parsedValue;
                                return next;
                              });
                            }}
                          />
                        </Space.Compact>
                      </div>
                    </Flex>
                  </Flex>
                </Card>
            ))}
          </Flex>
        )}
      </Content>

      <Footer />
    </>
  );
}
